import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Stepper, 
  Step, 
  StepLabel,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Stack,
  LinearProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Tooltip,
  useTheme,
  useMediaQuery,
  Autocomplete
} from '@mui/material';
import AudioUploader from './AudioUploader';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { useNotifications } from '../../contexts/NotificationContext';
import meetingService from '../../services/meetingService';
import modelsService from '../../services/modelsService';
import transcriptionService from '../../services/transcriptionService';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import SecurityIcon from '@mui/icons-material/Security';
import InfoIcon from '@mui/icons-material/Info';
import { uploadFile } from '../../utils/storageUtils';
import OneDriveFilePickerDialog from './OneDriveFilePickerDialog';
import TeamsRecordingPicker from './TeamsRecordingPicker';
import MicrosoftIcon from '@mui/icons-material/AccountBox';
import TeamsIcon from '@mui/icons-material/VideoCall';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import graphService from '../../services/graphService';
import authService from '../../services/authService';
import BaseDialog from './BaseDialog';
import BaseTextField from './BaseTextField';
import BaseButton from './BaseButton';
import storageUtils from '../../utils/storageUtils';
import ProjectSelector from './ProjectSelector';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const steps = ['Fichiers', 'Informations de base', 'Options avancées'];

const NewMeetingDialog = ({ open, onClose, onSuccess }) => {
  const { currentUser } = useAuth();
  const { addMeeting, setError } = useApp();
  const { addNotification, removeErrorNotificationsByPattern } = useNotifications();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isAdmin = currentUser && (currentUser.profile?.isAdmin || currentUser.isAdmin);

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [meetingType, setMeetingType] = useState('recorded');
  const [meetingData, setMeetingData] = useState({
    title: '',
    date: new Date().toISOString().slice(0, 10),
    context: '',
    participants: [],
    files: [],
    audioUrls: [],
    isConfidential: false,
    selectedModelId: null,
    projectId: null,
    autoGenerateSummary: false,
    summaryModelId: null
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [newParticipant, setNewParticipant] = useState({ displayName: '', title: '' });
    const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [oneDriveDialogOpen, setOneDriveDialogOpen] = useState(false);
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);

  // Écouter les modèles disponibles
  useEffect(() => {
    if (!currentUser) return;
    
    const unsubscribe = modelsService.getAllModels((models) => {
      // Trier les modèles par ordre alphabétique
      const sortedModels = models.sort((a, b) => {
        return a.title.localeCompare(b.title, 'fr', { sensitivity: 'base' });
      });
      setAvailableModels(sortedModels);
    });

    return unsubscribe;
  }, [currentUser]);

  // Ajouter automatiquement l'utilisateur connecté aux participants
  useEffect(() => {
    if (!currentUser || meetingData.participants.length > 0) return;
    
    // Extraction du nom plus robuste pour iOS
    const userName = 
      currentUser.displayName || 
      currentUser.profile?.displayName ||
      currentUser.email?.split('@')[0] ||
      'Moi';
    
    // Extraction du poste
    const userTitle = 
      currentUser.jobTitle || 
      currentUser.profile?.jobTitle || 
      '';
    
    setMeetingData(prev => ({
      ...prev,
      participants: [{
        displayName: userName,
        title: userTitle
      }]
    }));
  }, [currentUser]); // Retirer meetingData.participants.length pour permettre la suppression

  // Reset du formulaire à la fermeture
  const handleClose = () => {
    setActiveStep(0);
    setMeetingType('');
    setMeetingData({
      title: '',
      date: new Date().toISOString().slice(0, 10),
      context: '',
      participants: [],
      files: [],
      audioUrls: [],
      isConfidential: false,
      selectedModelId: null,
      projectId: null,
      autoGenerateSummary: false,
      summaryModelId: null
    });
    setValidationErrors({});
    setNewParticipant({ displayName: '', title: '' });
    setUploading(false);
    setUploadProgress({});
    setSelectedModel(null);

    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMeetingData({
      ...meetingData,
      [name]: value
    });
    
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: ''
      });
    }
  };

  const handleAddParticipant = () => {
    if (!newParticipant.displayName.trim()) return;
    setMeetingData({
      ...meetingData,
      participants: [...meetingData.participants, { ...newParticipant }]
    });
    setNewParticipant({ displayName: '', title: '' });
  };

  const handleRemoveParticipant = (index) => {
    const updatedParticipants = [...meetingData.participants];
    updatedParticipants.splice(index, 1);
    setMeetingData({
      ...meetingData,
      participants: updatedParticipants
    });
  };

  // Fonction utilitaire pour extraire les métadonnées d'un fichier
  const extractFileMetadata = (file) => {
    // Extraire le nom sans extension
    const nameWithoutExtension = file.name.replace(/\.[^/.]+$/, "");
    
    // Formater la date en YYYY-MM-DD pour l'input date
    let fileDate;
    if (file.lastModified) {
      // Fichier local
      fileDate = new Date(file.lastModified);
    } else if (file.lastModifiedDateTime) {
      // Fichier OneDrive
      fileDate = new Date(file.lastModifiedDateTime);
    } else {
      // Fallback sur la date actuelle
      fileDate = new Date();
    }
    
    const formattedDate = fileDate.toISOString().split('T')[0];
    
    return {
      suggestedTitle: nameWithoutExtension,
      suggestedDate: formattedDate
    };
  };

  // Fonction pour pré-remplir automatiquement les champs si vides
  const autoFillFieldsFromFile = (file) => {
    const isFirstFile = meetingData.files.length === 0;
    const titleIsEmpty = !meetingData.title.trim();
    const dateIsEmpty = !meetingData.date;
    
    if (isFirstFile && (titleIsEmpty || dateIsEmpty)) {
      const metadata = extractFileMetadata(file);
      
      setMeetingData(prev => ({
        ...prev,
        title: titleIsEmpty ? metadata.suggestedTitle : prev.title,
        date: dateIsEmpty ? metadata.suggestedDate : prev.date
      }));
      
      console.log('🎯 Pré-remplissage automatique:', {
        titre: titleIsEmpty ? metadata.suggestedTitle : 'conservé',
        date: dateIsEmpty ? metadata.suggestedDate : 'conservée'
      });
    }
  };

  const handleFileSelect = (file) => {
    if (Array.isArray(file)) {
      // Plusieurs fichiers sélectionnés en une fois
      // Pré-remplir avec le premier fichier
      if (file.length > 0) {
        autoFillFieldsFromFile(file[0]);
      }

      setMeetingData(prev => ({
        ...prev,
        files: [...prev.files, ...file]
      }));
    } else {
      // Un seul fichier
      // Pré-remplir automatiquement
      autoFillFieldsFromFile(file);

      setMeetingData(prev => ({
        ...prev,
        files: [...prev.files, file]
      }));
    }
  };

  const handleRemoveFile = (index) => {
    const updatedFiles = [...meetingData.files];
    updatedFiles.splice(index, 1);
    
    setMeetingData({
      ...meetingData,
      files: updatedFiles
    });
  };

  // Fonction pour réordonner les fichiers (drag & drop)
  const handleFileReorder = (fromIndex, toIndex) => {
    const updatedFiles = [...meetingData.files];
    const [movedFile] = updatedFiles.splice(fromIndex, 1);
    updatedFiles.splice(toIndex, 0, movedFile);
    
    setMeetingData({
      ...meetingData,
      files: updatedFiles
    });
  };

  // Gestion du drag & drop
  const [draggedIndex, setDraggedIndex] = useState(null);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      handleFileReorder(draggedIndex, dropIndex);
    }
    setDraggedIndex(null);
  };

  // Gestion du touch pour mobile
  const [touchStartY, setTouchStartY] = useState(null);
  const [touchCurrentY, setTouchCurrentY] = useState(null);

  const handleTouchStart = (e, index) => {
    setDraggedIndex(index);
    setTouchStartY(e.touches[0].clientY);
    setTouchCurrentY(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    if (touchStartY !== null) {
      setTouchCurrentY(e.touches[0].clientY);
    }
  };

  const handleTouchEnd = () => {
    if (touchStartY !== null && touchCurrentY !== null && draggedIndex !== null) {
      const deltaY = touchCurrentY - touchStartY;
      const threshold = 50; // Minimum distance to trigger reorder
      
      if (Math.abs(deltaY) > threshold) {
        const direction = deltaY > 0 ? 1 : -1; // Down or up
        const newIndex = Math.max(0, Math.min(meetingData.files.length - 1, draggedIndex + direction));
        
        if (newIndex !== draggedIndex) {
          handleFileReorder(draggedIndex, newIndex);
        }
      }
    }
    
    setDraggedIndex(null);
    setTouchStartY(null);
    setTouchCurrentY(null);
  };

  const handleOneDriveFileSelected = (fileMeta) => {
    if (Array.isArray(fileMeta)) {
      // Plusieurs fichiers sélectionnés depuis OneDrive
      // Pré-remplir avec le premier fichier OneDrive
      if (fileMeta.length > 0) {
        autoFillFieldsFromFile(fileMeta[0]);
      }

      const newFiles = fileMeta.map(file => ({
        ...file,
        type: file.mimeType,
        fromOneDrive: true
      }));
      setMeetingData(prev => ({
        ...prev,
        files: [...prev.files, ...newFiles]
      }));
    } else {
      // Un seul fichier depuis OneDrive
      
      // Pré-remplir automatiquement avec le fichier OneDrive
      autoFillFieldsFromFile(fileMeta);
      
      setMeetingData(prev => ({
        ...prev,
        files: [...prev.files, { 
          ...fileMeta, 
          type: fileMeta.mimeType,
          fromOneDrive: true 
        }]
      }));
    }
    setOneDriveDialogOpen(false);
  };

  // Gestion de la sélection d'un enregistrement Teams
  const handleTeamsFileSelected = (file) => {
    // Pré-remplir automatiquement avec les métadonnées Teams
    const isFirstFile = meetingData.files.length === 0;
    const titleIsEmpty = !meetingData.title.trim();
    const dateIsEmpty = !meetingData.date;

    if (isFirstFile && (titleIsEmpty || dateIsEmpty)) {
      setMeetingData(prev => ({
        ...prev,
        title: titleIsEmpty && file.meetingSubject ? file.meetingSubject : prev.title,
        date: dateIsEmpty && file.meetingDate ? file.meetingDate : prev.date,
        participants: file.suggestedParticipants && file.suggestedParticipants.length > 0
          ? [...file.suggestedParticipants]
          : prev.participants
      }));
    }

    // Ajouter le fichier à la liste
    setMeetingData(prev => ({
      ...prev,
      files: [...prev.files, file]
    }));
  };

  const handleModelSelection = (model) => {
    setSelectedModel(model);
    if (model && model.participants && model.participants.length > 0) {
      setMeetingData(prev => ({
        ...prev,
        participants: [...model.participants],
        selectedModelId: model.id
      }));
    } else if (model) {
      setMeetingData(prev => ({
        ...prev,
        selectedModelId: model.id
      }));
    } else {
      setMeetingData(prev => ({
        ...prev,
        selectedModelId: null
      }));
    }
  };

  const validateStep = (step) => {
    const errors = {};

    if (step === 0) {
      if (meetingData.files.length === 0) {
        errors.files = 'Au moins un fichier audio/vidéo est requis';
      }
    }

    if (step === 1) {
      if (!meetingData.title.trim()) {
        errors.title = 'Le titre est requis';
      }
      if (!meetingData.date) {
        errors.date = 'La date est requise';
      }
      if (!Array.isArray(meetingData.participants) || meetingData.participants.length === 0) {
        errors.participants = 'Veuillez ajouter au moins un participant.';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (!validateStep(activeStep)) {
      return;
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const uploadSingleFileWithRetry = async (file, index, maxRetries = 3) => {
    let lastError = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await uploadSingleFile(file, index, attempt);
      } catch (error) {
        lastError = error;
        
        // Vérifier si c'est une erreur temporaire qui mérite un retry
        const isRetryableError = error.message.includes('timeout') || 
                                 error.message.includes('network') ||
                                 error.message.includes('Load failed') ||
                                 error.message.includes('500') ||
                                 error.message.includes('502') ||
                                 error.message.includes('503');
        
        // Si ce n'est pas une erreur temporaire ou si c'est le dernier essai, on abandonne
        if (!isRetryableError || attempt >= maxRetries) {
          throw error;
        }
        
        // Attendre avant le prochain essai (délai croissant: 1s, 2s, 4s)
        const delay = Math.pow(2, attempt - 1) * 1000;
        setUploadProgress(prev => ({
          ...prev,
          [index]: { 
            ...prev[index], 
            progress: 0, 
            status: `Erreur temporaire, nouvelle tentative dans ${delay/1000}s... (${attempt}/${maxRetries})`,
            error: null
          }
        }));
        
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    throw lastError;
  };

  const uploadSingleFile = async (file, index, attempt = 1) => {
    if (file.fromOneDrive) {
      setUploadProgress(prev => ({
        ...prev,
        [index]: { progress: 0, source: 'onedrive', error: null }
      }));
      
      try {
        const statusPrefix = attempt > 1 ? `Tentative ${attempt} - ` : '';
        setUploadProgress(prev => ({
          ...prev,
          [index]: { ...prev[index], progress: 5, status: `${statusPrefix}Récupération URL OneDrive...` }
        }));
        
        const downloadUrl = await graphService.getDownloadUrl(file.id);
        
        setUploadProgress(prev => ({
          ...prev,
          [index]: { ...prev[index], progress: 10, status: 'Téléchargement depuis OneDrive...' }
        }));
        
        const response = await fetch(downloadUrl);
        if (!response.ok) {
          throw new Error(`Erreur lors du téléchargement OneDrive: ${response.status} ${response.statusText}`);
        }
        
        const contentLength = response.headers.get('Content-Length');
        const totalSize = contentLength ? parseInt(contentLength, 10) : file.size || 0;
        
        const reader = response.body.getReader();
        const chunks = [];
        let downloadedBytes = 0;
        
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) break;
          
          chunks.push(value);
          downloadedBytes += value.length;
          
          if (totalSize > 0) {
            const downloadProgress = Math.min(30, Math.round((downloadedBytes / totalSize) * 30));
            setUploadProgress(prev => ({
              ...prev,
              [index]: { 
                ...prev[index], 
                progress: 10 + downloadProgress,
                status: `Téléchargement OneDrive: ${Math.round((downloadedBytes / totalSize) * 100)}%`
              }
            }));
          }
        }
        
        const fileBlob = new Blob(chunks);
        const fileForUpload = new File([fileBlob], file.name, {
          type: file.mimeType || file.type || 'audio/mpeg'
        });
        
        setUploadProgress(prev => ({
          ...prev,
          [index]: { ...prev[index], progress: 45, status: 'Préparation envoi vers le serveur...' }
        }));
        
        const firebaseUrl = await uploadFile(
          fileForUpload,
          'audio',
          (progress) => {
            const totalProgress = 45 + Math.round(progress * 0.55);
            setUploadProgress(prev => ({
              ...prev,
              [index]: { 
                ...prev[index], 
                progress: totalProgress,
                status: `Envoi vers le serveur: ${progress}%`
              }
            }));
          }
        );
        
        setUploadProgress(prev => ({
          ...prev,
          [index]: { ...prev[index], progress: 100, status: 'Terminé ✅' }
        }));
        
        return firebaseUrl;
        
      } catch (error) {
        setUploadProgress(prev => ({
          ...prev,
          [index]: { 
            ...prev[index], 
            error: `Erreur: ${error.message}`, 
            progress: 0,
            status: 'Échec ❌'
          }
        }));
        
        // Message d'erreur plus informatif selon le type d'erreur
        let errorMessage = `Échec upload OneDrive ${file.name}: ${error.message}`;
        
        if (error.message.includes('authentication') || error.message.includes('401')) {
          errorMessage = `Échec upload OneDrive ${file.name}: Session Azure expirée. Reconnectez-vous et réessayez.`;
        } else if (error.message.includes('network') || error.message.includes('timeout')) {
          errorMessage = `Échec upload OneDrive ${file.name}: Problème de connexion réseau. Vérifiez votre connexion et réessayez.`;
        } else if (error.message.includes('Load failed')) {
          errorMessage = `Échec upload OneDrive ${file.name}: Erreur de chargement du fichier. Le fichier est peut-être corrompu ou trop volumineux.`;
        }
        
        throw new Error(errorMessage);
      }
    } else {
      setUploadProgress(prev => ({
        ...prev,
        [index]: { progress: 0, source: 'local', error: null }
      }));
      
      return await uploadFile(
        file,
        'audio',
        (progress) => {
          setUploadProgress(prev => ({
            ...prev,
            [index]: { ...prev[index], progress, status: `Upload: ${progress}%` }
          }));
        }
      );
    }
  };

  const uploadAllFiles = async () => {
    const urls = [];
    setUploading(true);
    
    try {
      for (let i = 0; i < meetingData.files.length; i++) {
        const url = await uploadSingleFileWithRetry(meetingData.files[i], i);
        urls.push(url);
      }
      return urls;
    } catch (error) {

      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      if (meetingData.files.length === 0) {
        throw new Error('Aucun fichier audio sélectionné');
      }

      const audioUrls = await uploadAllFiles();
      
      if (!audioUrls || audioUrls.length === 0) {
        throw new Error('Échec de l\'upload des fichiers audio');
      }

      const newMeetingData = {
        title: meetingData.title,
        date: meetingData.date,
        participants: meetingData.participants,
        context: meetingData.context,
        audioUrl: audioUrls[0],
        fileName: meetingData.files[0].name,
        fileSize: meetingData.files[0].size,
        fileType: meetingData.files[0].type,
        additionalFiles: meetingData.files.slice(1).map((file, index) => ({
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          audioUrl: audioUrls[index + 1]
        })),
        status: 'pending',
        isConfidential: meetingData.isConfidential,
        selectedModelId: meetingData.selectedModelId,
        autoGenerateSummary: meetingData.autoGenerateSummary,
        summaryModelId: meetingData.summaryModelId,
        projectId: meetingData.projectId
      };
      
      const createdMeeting = await meetingService.createMeeting(newMeetingData);
      
      // Démarrer automatiquement la transcription (mono ou multi-fichiers)
      try {
        if (!Array.isArray(meetingData.participants) || meetingData.participants.length === 0) {
          throw new Error('Veuillez renseigner au moins un participant avant de démarrer la transcription.');
        }

        let transcriptionResult;

        // Récupérer le token Azure pour l'envoi d'email automatique après génération du résumé
        const azureToken = authService.getAzureToken();

        if (audioUrls.length > 1) {
          // Multi-fichiers : utiliser la nouvelle fonction
          const audioFilesData = audioUrls.map((url, index) => ({
            audioUrl: url,
            fileName: meetingData.files[index].name,
            fileSize: meetingData.files[index].size,
            fileType: meetingData.files[index].type
          }));

          transcriptionResult = await transcriptionService.startMultipleTranscriptions(
            createdMeeting.id,
            audioFilesData,
            { azureToken }
          );
        } else {
          // Mono-fichier : comportement existant
          transcriptionResult = await transcriptionService.startTranscription(
            createdMeeting.id,
            createdMeeting.audioUrl,
            { azureToken }
          );
        }
        
        if (transcriptionResult.success) {
          const updatedMeeting = {
            ...createdMeeting,
            status: 'transcribing'
          };
          addMeeting(updatedMeeting);
          
          // Notification de démarrage de transcription en mode progress (animée)
          try {
            await addNotification({
              message: `🎤 Transcription en cours pour "${createdMeeting.title}"...`,
              type: 'progress',
              category: 'transcription',
              metadata: { 
                meetingTitle: createdMeeting.title, 
                meetingId: createdMeeting.id,
                isProgress: true
              }
            });
          } catch (notifError) {

          }
        } else {
          addMeeting(createdMeeting);
          
          // Notification d'erreur de transcription
          try {
            await addNotification({
              message: `❌ Impossible de démarrer la transcription pour "${createdMeeting.title}": ${transcriptionResult.message || 'Erreur inconnue'}`,
              type: 'error',
              category: 'transcription',
              metadata: { meetingTitle: createdMeeting.title, meetingId: createdMeeting.id, errorMessage: transcriptionResult.message }
            });
          } catch (notifError) {

          }
        }
      } catch (transcriptionError) {

        addMeeting(createdMeeting);
        
        // Notification d'erreur de transcription
        try {
          await addNotification({
            message: `❌ Erreur lors du démarrage de la transcription pour "${createdMeeting.title}": ${transcriptionError.message || 'Erreur inconnue'}`,
            type: 'error',
            category: 'transcription',
            metadata: { meetingTitle: createdMeeting.title, meetingId: createdMeeting.id, errorMessage: transcriptionError.message }
          });
        } catch (notifError) {

        }
      }
      
      if (onSuccess) {
        onSuccess(createdMeeting.id);
              }
        handleClose();

        // Nettoyer les notifications d'erreur obsolètes pour cette réunion
        try {
          await removeErrorNotificationsByPattern(createdMeeting.title);
          await removeErrorNotificationsByPattern('Erreur lors de la création de la réunion');
        } catch (cleanupError) {
          console.error('Erreur lors du nettoyage des notifications:', cleanupError);
        }

        // Créer la notification de création de réunion
        try {
          await addNotification({
            message: `✅ Réunion "${createdMeeting.title}" créée avec succès`,
            type: 'success',
            category: 'meeting',
            metadata: { meetingTitle: createdMeeting.title, meetingId: createdMeeting.id }
          });
        } catch (notifError) {

        }
          } catch (error) {

        setError('Erreur lors de la création de la réunion. Veuillez réessayer.');
        
        // Créer la notification d'erreur
        try {
          await addNotification({
            message: `❌ Erreur lors de la création de la réunion "${meetingData.title}": ${error.message || 'Erreur inconnue'}`,
            type: 'error',
            category: 'meeting',
            metadata: { meetingTitle: meetingData.title, errorMessage: error.message }
          });
        } catch (notifError) {

        }
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                En important un fichier audio/vidéo, vous certifiez avoir recueilli le consentement de l'ensemble des interlocuteurs pour être enregistré.
              </Alert>
              
              <Box sx={{ mb: 2 }}>
                <AudioUploader onFileSelect={handleFileSelect} />
                <Box sx={{ textAlign: 'center', my: 1, color: 'text.secondary', fontSize: '0.8rem' }}>
                  — ou —
                </Box>
                <BaseButton
                  variant="outlined"
                  startIcon={<MicrosoftIcon />}
                  fullWidth
                  onClick={() => setOneDriveDialogOpen(true)}
                  size="small"
                >
                  Importer depuis OneDrive
                </BaseButton>
              </Box>
              
              {meetingData.files.length > 0 && (
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Fichiers sélectionnés ({meetingData.files.length}):
                    {meetingData.files.length > 1 && (
                      <Typography variant="caption" color="primary" sx={{ ml: 1, display: 'block' }}>
                        ↕️ Glissez-déposez pour réordonner les fichiers
                      </Typography>
                    )}
                  </Typography>
                  {meetingData.files.map((file, index) => (
                    <Paper 
                      key={index} 
                      variant="outlined" 
                      draggable={meetingData.files.length > 1}
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, index)}
                      onTouchStart={(e) => handleTouchStart(e, index)}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                      sx={{ 
                        p: 1, 
                        mb: 1, 
                        display: 'flex', 
                        alignItems: 'center',
                        cursor: meetingData.files.length > 1 ? 'grab' : 'default',
                        opacity: draggedIndex === index ? 0.5 : 1,
                        borderLeft: meetingData.files.length > 1 ? '4px solid' : 'none',
                        borderLeftColor: index === 0 ? '#4caf50' : 
                                        index === 1 ? '#2196f3' : 
                                        index === 2 ? '#ff9800' : '#9c27b0',
                        '&:hover': {
                          backgroundColor: meetingData.files.length > 1 ? 'action.hover' : 'transparent'
                        },
                        touchAction: 'none' // Disable default touch scrolling
                      }}
                    >
                      {meetingData.files.length > 1 && (
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center', 
                          mr: 2,
                          minWidth: 40
                        }}>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              backgroundColor: index === 0 ? '#4caf50' : 
                                              index === 1 ? '#2196f3' : 
                                              index === 2 ? '#ff9800' : '#9c27b0',
                              color: 'white',
                              borderRadius: '50%',
                              width: 24,
                              height: 24,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.875rem',
                              fontWeight: 'bold'
                            }}
                          >
                            {index + 1}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                            {index === 0 ? '1er' : index === 1 ? '2ème' : index === 2 ? '3ème' : `${index + 1}ème`}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: index === 0 ? 'bold' : 'normal' }}>
                          {file.name}
                          {index === 0 && meetingData.files.length > 1 && (
                            <Typography component="span" variant="caption" color="primary" sx={{ ml: 1 }}>
                              (Premier fichier)
                            </Typography>
                          )}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {(file.size / (1024 * 1024)).toFixed(2)} MB
                        </Typography>
                      </Box>
                      <IconButton 
                        size="small"
                        color="error" 
                        onClick={() => handleRemoveFile(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Paper>
                  ))}
                </Box>
              )}
              
              {validationErrors.files && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {validationErrors.files}
                </Alert>
              )}
            </Box>
          );

      case 1:
        return (
          <Box 
            sx={{ 
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              borderRadius: '8px',
              p: 4,
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}
          >
            <Grid container spacing={4}>
              {/* Sélection de modèle de prompt */}
              <Grid item xs={12}>
                <Box sx={{
                  background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                  borderRadius: '12px',
                  p: 3,
                  border: '2px solid #e2e8f0',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      mb: 2,
                      fontWeight: 600,
                      color: '#1e293b',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      fontFamily: '"Inter", sans-serif',
                      fontSize: '16px'
                    }}
                  >
                    ✨ Modèle (optionnel)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Sélectionnez un modèle pour pré-remplir les participants et personnaliser le résumé.
                  </Typography>
                  <Autocomplete
                    value={selectedModel}
                    onChange={(_, newValue) => handleModelSelection(newValue)}
                    options={availableModels}
                    getOptionLabel={(option) => option.title || ''}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    renderInput={(params) => (
                      <BaseTextField
                        {...params}
                        label="Choisir un modèle"
                        placeholder="Rechercher un modèle..."
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: '#ffffff',
                            '& fieldset': {
                              borderColor: '#cbd5e1'
                            },
                            '&:hover fieldset': {
                              borderColor: '#94a3b8'
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#3b82f6',
                              borderWidth: '2px'
                            }
                          }
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                          <Box sx={{ 
                            background: option.createdBy === currentUser?.uid 
                              ? 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)' // Vert pour mes modèles
                              : 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)', // Violet pour les autres
                            borderRadius: '6px',
                            p: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Typography sx={{ fontSize: '12px' }}>
                              {option.createdBy === currentUser?.uid ? '👤' : '✨'}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography 
                                variant="body2" 
                                fontWeight={500}
                                sx={{ 
                                  fontFamily: '"Inter", sans-serif',
                                  color: option.createdBy === currentUser?.uid ? '#2e7d32' : '#1e293b'
                                }}
                              >
                                {option.title}
                              </Typography>
                              {option.createdBy === currentUser?.uid && (
                                <Chip 
                                  label="Mes modèles" 
                                  size="small" 
                                  sx={{
                                    height: 16,
                                    fontSize: '9px',
                                    background: '#e8f5e8',
                                    color: '#2e7d32',
                                    border: '1px solid #4caf50',
                                    fontWeight: 600
                                  }}
                                />
                              )}
                            </Box>
                            {option.participants && option.participants.length > 0 && (
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: '#64748b',
                                  fontFamily: '"Inter", sans-serif'
                                }}
                              >
                                {option.participants.length} participant(s) • {option.tags?.length || 0} tag(s)
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    )}
                    noOptionsText="Aucun modèle trouvé"
                    size="medium"
                  />
                  {selectedModel && (
                    <Box sx={{ mt: 2, p: 2, background: '#ffffff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      <Typography variant="body2" color="primary" fontWeight={500} sx={{ mb: 1 }}>
                        📝 {selectedModel.title}
                      </Typography>
                      {selectedModel.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          {selectedModel.description}
                        </Typography>
                      )}
                      {selectedModel.participants && selectedModel.participants.length > 0 && (
                        <Typography variant="caption" color="success.main">
                          ✅ {selectedModel.participants.length} participant(s) pré-rempli(s)
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <BaseTextField
                  name="title"
                  label="📝 Titre de la réunion"
                  fullWidth
                  value={meetingData.title}
                  onChange={handleChange}
                  error={!!validationErrors.title}
                  helperText={validationErrors.title}
                  required
                  size="medium"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                      fontWeight: 400,
                      fontSize: '14px',
                      backgroundColor: '#ffffff',
                      '& fieldset': {
                        borderColor: '#cbd5e1'
                      },
                      '&:hover fieldset': {
                        borderColor: '#94a3b8'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3b82f6',
                        borderWidth: '2px'
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontWeight: 500,
                      fontSize: '14px',
                      color: '#475569',
                      fontFamily: '"Inter", sans-serif',
                      '&.Mui-focused': { color: '#3b82f6' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <BaseTextField
                  name="date"
                  label="📅 Date de la réunion"
                  type="date"
                  fullWidth
                  value={meetingData.date}
                  onChange={handleChange}
                  error={!!validationErrors.date}
                  helperText={validationErrors.date}
                  required
                  size="medium"
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                      fontWeight: 400,
                      fontSize: '14px',
                      backgroundColor: '#ffffff',
                      '& fieldset': {
                        borderColor: '#cbd5e1'
                      },
                      '&:hover fieldset': {
                        borderColor: '#94a3b8'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3b82f6',
                        borderWidth: '2px'
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontWeight: 500,
                      fontSize: '14px',
                      color: '#475569',
                      fontFamily: '"Inter", sans-serif',
                      '&.Mui-focused': { color: '#3b82f6' }
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography 
                  variant="subtitle2" 
                  gutterBottom 
                  sx={{ 
                    mt: 1, 
                    mb: 2.5,
                    fontWeight: 600,
                    color: '#1e293b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    fontFamily: '"Inter", sans-serif',
                    fontSize: '15px'
                  }}
                >
                  👥 Participants (obligatoire)
                </Typography>
                <Stack direction={isMobile ? "column" : "row"} spacing={2} sx={{ mb: 2.5 }}>
                  <BaseTextField
                    label="Nom"
                    value={newParticipant.displayName}
                    onChange={e => setNewParticipant(prev => ({ ...prev, displayName: e.target.value }))}
                    size="medium"
                    sx={{ 
                      flex: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 400,
                        fontSize: '14px',
                        backgroundColor: '#ffffff',
                        '& fieldset': { borderColor: '#cbd5e1' },
                        '&:hover fieldset': { borderColor: '#94a3b8' },
                        '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: '2px' }
                      },
                      '& .MuiInputLabel-root': {
                        fontWeight: 500,
                        fontSize: '14px',
                        color: '#475569',
                        fontFamily: '"Inter", sans-serif'
                      }
                    }}
                  />
                  <BaseTextField
                    label="Poste"
                    value={newParticipant.title}
                    onChange={e => setNewParticipant(prev => ({ ...prev, title: e.target.value }))}
                    size="medium"
                    sx={{ 
                      flex: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 400,
                        fontSize: '14px',
                        backgroundColor: '#ffffff',
                        '& fieldset': { borderColor: '#cbd5e1' },
                        '&:hover fieldset': { borderColor: '#94a3b8' },
                        '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: '2px' }
                      },
                      '& .MuiInputLabel-root': {
                        fontWeight: 500,
                        fontSize: '14px',
                        color: '#475569',
                        fontFamily: '"Inter", sans-serif'
                      }
                    }}
                  />
                  <BaseButton 
                    variant="contained" 
                    size="medium"
                    onClick={handleAddParticipant}
                    disabled={!newParticipant.displayName.trim()}
                    sx={{ 
                      px: 3, 
                      py: 1.5,
                      minWidth: 'auto',
                      alignSelf: isMobile ? 'flex-start' : 'stretch',
                      borderRadius: '6px',
                      fontFamily: '"Inter", sans-serif',
                      fontWeight: 500,
                      textTransform: 'none'
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </BaseButton>
                </Stack>
                
                {meetingData.participants.length > 0 && (
                  <Box sx={{ 
                    maxHeight: 140, 
                    overflow: 'auto', 
                    background: '#ffffff',
                    border: '1px solid #e2e8f0', 
                    borderRadius: '8px',
                    '&::-webkit-scrollbar': {
                      width: '6px'
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#f1f5f9'
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#cbd5e1',
                      borderRadius: '3px'
                    }
                  }}>
                    {meetingData.participants.map((p, idx) => (
                      <Box 
                        key={idx} 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center', 
                          p: 2.5, 
                          borderBottom: idx < meetingData.participants.length - 1 ? '1px solid #f1f5f9' : 'none',
                          '&:hover': {
                            background: '#f8fafc'
                          },
                          transition: 'background 0.2s ease'
                        }}
                      >
                        <Box>
                          <Typography 
                            variant="body2" 
                            fontWeight={500} 
                            sx={{ 
                              color: '#1e293b', 
                              mb: 0.5,
                              fontFamily: '"Inter", sans-serif',
                              fontSize: '14px'
                            }}
                          >
                            {p.displayName}
                          </Typography>
                          {p.title && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#64748b', 
                                fontFamily: '"Inter", sans-serif',
                                fontSize: '13px'
                              }}
                            >
                              {p.title}
                            </Typography>
                          )}
                        </Box>
                        <IconButton 
                          size="small" 
                          onClick={() => handleRemoveParticipant(idx)}
                          sx={{
                            color: '#dc2626',
                            background: '#fef2f2',
                            borderRadius: '6px',
                            width: 32,
                            height: 32,
                            '&:hover': {
                              background: '#fee2e2',
                              transform: 'scale(1.05)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
                
                {validationErrors.participants && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {validationErrors.participants}
                  </Alert>
                )}
              </Grid>
              
              <Grid item xs={12}>
                <BaseTextField
                  name="context"
                  label="💭 Contexte de la réunion"
                  fullWidth
                  multiline
                  rows={3}
                  value={meetingData.context}
                  onChange={handleChange}
                  placeholder="Décrivez brièvement le contexte, les objectifs ou les sujets abordés..."
                  size="medium"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                      fontWeight: 400,
                      fontSize: '14px',
                      backgroundColor: '#ffffff',
                      '& fieldset': {
                        borderColor: '#cbd5e1'
                      },
                      '&:hover fieldset': {
                        borderColor: '#94a3b8'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3b82f6',
                        borderWidth: '2px'
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontWeight: 500,
                      fontSize: '14px',
                      color: '#475569',
                      fontFamily: '"Inter", sans-serif',
                      '&.Mui-focused': { color: '#3b82f6' }
                    },
                    '& .MuiOutlinedInput-input::placeholder': {
                      color: '#94a3b8',
                      opacity: 1,
                      fontFamily: '"Inter", sans-serif'
                    }
                  }}
                />
              </Grid>

              {/* Option Confidentialité */}
              <Grid item xs={12}>
                <Box 
                  sx={{ 
                    background: '#ffffff',
                    borderRadius: '8px',
                    p: 3,
                    border: meetingData.isConfidential 
                      ? '2px solid #8b5cf6' 
                      : '1px solid #e2e8f0',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      borderColor: meetingData.isConfidential ? '#7c3aed' : '#cbd5e1',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1 }}>
                      <SecurityIcon 
                        sx={{ 
                          color: meetingData.isConfidential ? '#8b5cf6' : '#64748b',
                          fontSize: '20px',
                          transition: 'color 0.2s ease',
                          mt: 0.25
                        }} 
                      />
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography 
                            variant="subtitle2" 
                            sx={{ 
                              fontWeight: 600,
                              color: meetingData.isConfidential ? '#8b5cf6' : '#1e293b',
                              transition: 'color 0.2s ease',
                              fontFamily: '"Inter", sans-serif',
                              fontSize: '15px',
                              lineHeight: 1.3
                            }}
                          >
                            Réunion confidentielle
                          </Typography>
                          <Tooltip
                            title={
                              <Box sx={{ p: 1 }}>
                                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                                  🔒 Mode Confidentiel
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.4 }}>
                                  • Seul vous aurez accès à cette réunion
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.4 }}>
                                  • Les administrateurs ne pourront pas la consulter
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.4 }}>
                                  • L'accès technique en base reste possible pour la maintenance
                                </Typography>
                              </Box>
                            }
                            arrow
                            placement="top"
                          >
                            <IconButton 
                              size="small"
                              sx={{ 
                                p: 0.25,
                                color: '#64748b',
                                '&:hover': {
                                  background: '#f1f5f9',
                                  color: '#3b82f6'
                                }
                              }}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#64748b',
                            fontFamily: '"Inter", sans-serif',
                            fontSize: '13px',
                            lineHeight: 1.4
                          }}
                        >
                          {meetingData.isConfidential 
                            ? "🔐 Accès exclusif - Visible uniquement par vous"
                            : "👥 Accès standard - Visible par vous et les administrateurs"
                          }
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Switch
                      checked={meetingData.isConfidential}
                      onChange={(e) => setMeetingData(prev => ({
                        ...prev,
                        isConfidential: e.target.checked
                      }))}
                      sx={{
                        width: 48,
                        height: 26,
                        padding: 0,
                        margin: 0,
                        '& .MuiSwitch-switchBase': {
                          padding: '3px',
                          margin: 0,
                          '&.Mui-checked': {
                            color: '#ffffff',
                            transform: 'translateX(22px)',
                            '& + .MuiSwitch-track': {
                              backgroundColor: '#8b5cf6',
                              opacity: 1,
                              border: 'none'
                            }
                          }
                        },
                        '& .MuiSwitch-thumb': {
                          width: 20,
                          height: 20,
                          backgroundColor: '#ffffff',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                        },
                        '& .MuiSwitch-track': {
                          borderRadius: 13,
                          backgroundColor: '#cbd5e1',
                          opacity: 1,
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          border: '1px solid #e2e8f0'
                        }
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Grid container spacing={3}>
              {/* Option de génération automatique du résumé */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    background: '#ffffff',
                    borderRadius: '8px',
                    p: 3,
                    border: meetingData.autoGenerateSummary
                      ? '2px solid #10b981'
                      : '1px solid #e2e8f0',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      borderColor: meetingData.autoGenerateSummary ? '#059669' : '#cbd5e1',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1 }}>
                      <AutoAwesomeIcon
                        sx={{
                          color: meetingData.autoGenerateSummary ? '#10b981' : '#64748b',
                          fontSize: '20px',
                          transition: 'color 0.2s ease',
                          mt: 0.25
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              color: meetingData.autoGenerateSummary ? '#10b981' : '#1e293b',
                              transition: 'color 0.2s ease',
                              fontFamily: '"Inter", sans-serif',
                              fontSize: '15px',
                              lineHeight: 1.3
                            }}
                          >
                            Générer automatiquement le résumé après transcription
                          </Typography>
                          <Tooltip
                            title={
                              <Box sx={{ p: 1 }}>
                                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                                  ✨ Résumé Automatique
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.4 }}>
                                  • Le résumé sera généré dès que la transcription est terminée
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.4 }}>
                                  • Bien définir les participants améliore la pertinence
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.4 }}>
                                  • Le système fonctionne très bien même sans identification des locuteurs
                                </Typography>
                              </Box>
                            }
                            arrow
                            placement="top"
                          >
                            <IconButton
                              size="small"
                              sx={{
                                p: 0.25,
                                color: '#64748b',
                                '&:hover': {
                                  background: '#f1f5f9',
                                  color: '#3b82f6'
                                }
                              }}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#64748b',
                            fontFamily: '"Inter", sans-serif',
                            fontSize: '13px',
                            lineHeight: 1.4,
                            mb: meetingData.autoGenerateSummary ? 2 : 0
                          }}
                        >
                          {meetingData.autoGenerateSummary
                            ? "✨ Le résumé sera automatiquement généré après la transcription"
                            : "Vous devrez générer manuellement le résumé après la transcription"
                          }
                        </Typography>

                        {meetingData.autoGenerateSummary && (
                          <Box sx={{ mt: 2 }}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Modèle</InputLabel>
                              <Select
                                value={meetingData.summaryModelId || ''}
                                onChange={(e) => setMeetingData(prev => ({
                                  ...prev,
                                  summaryModelId: e.target.value
                                }))}
                                label="Modèle"
                              >
                                <MenuItem value="" disabled>
                                  Sélectionnez un modèle
                                </MenuItem>
                                {availableModels.map((model) => (
                                  <MenuItem key={model.id} value={model.id}>
                                    {model.title}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Alert severity="info" sx={{ mt: 1.5 }} icon={<InfoIcon />}>
                              💡 Astuce : Bien définir les participants améliore la pertinence du résumé,
                              mais le système fonctionne très bien sans.
                            </Alert>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    <Switch
                      checked={meetingData.autoGenerateSummary}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        setMeetingData(prev => {
                          let defaultModelId = prev.summaryModelId;

                          if (isChecked && !defaultModelId) {
                            defaultModelId = prev.selectedModelId || 'T4E9FcWJm8zjb8oK8HcW';
                          }

                          return {
                            ...prev,
                            autoGenerateSummary: isChecked,
                            summaryModelId: isChecked ? defaultModelId : prev.summaryModelId
                          };
                        });
                      }}
                      sx={{
                        width: 48,
                        height: 26,
                        padding: 0,
                        margin: 0,
                        '& .MuiSwitch-switchBase': {
                          padding: '3px',
                          margin: 0,
                          '&.Mui-checked': {
                            color: '#ffffff',
                            transform: 'translateX(22px)',
                            '& + .MuiSwitch-track': {
                              backgroundColor: '#10b981',
                              opacity: 1,
                              border: 'none'
                            }
                          }
                        },
                        '& .MuiSwitch-thumb': {
                          width: 20,
                          height: 20,
                          backgroundColor: '#ffffff',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                        },
                        '& .MuiSwitch-track': {
                          borderRadius: 13,
                          backgroundColor: '#e2e8f0',
                          opacity: 1,
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          border: '1px solid #e2e8f0'
                        }
                      }}
                    />
                  </Box>
                </Box>
              </Grid>

              {/* Sélecteur de projet avec message informatif (admin only) */}
              {isAdmin && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }} icon={<InfoIcon />}>
                    💡 <strong>Astuce Projet</strong> : En associant cette réunion à un projet, l'IA recevra le contexte des réunions précédentes du même projet pour générer un résumé plus précis et cohérent.
                  </Alert>
                  <ProjectSelector
                    value={meetingData.projectId ? { id: meetingData.projectId } : null}
                    onChange={(project) => setMeetingData(prev => ({
                      ...prev,
                      projectId: project?.id || null
                    }))}
                    disabled={loading}
                  />
                </Grid>
              )}
            </Grid>

            {uploading && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Upload en cours...
                </Typography>
                {meetingData.files.map((file, index) => {
                  const progress = uploadProgress[index];
                  const hasError = progress?.error;
                  const isCompleted = progress?.progress === 100;

                  return (
                    <Box key={index} sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption">{file.name}</Typography>
                        <Typography variant="caption" color={hasError ? "error" : isCompleted ? "success.main" : "primary"}>
                          {progress?.progress || 0}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={progress?.progress || 0}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          '& .MuiLinearProgress-bar': {
                            bgcolor: hasError ? 'error.main' : isCompleted ? 'success.main' : 'primary.main'
                          }
                        }}
                      />
                      {progress?.status && (
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                          {progress.status}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        );
      
      default:
        return 'Étape inconnue';
    }
  };

  // Actions du dialog selon l'étape
  const getDialogActions = () => {
    return (
      <>
        <BaseButton
          variant="outlined"
          onClick={activeStep === 0 ? handleClose : handleBack}
          disabled={loading || uploading}
        >
          {activeStep === 0 ? 'Annuler' : 'Retour'}
        </BaseButton>
        
        {activeStep === steps.length - 1 ? (
          <BaseButton 
            gradient
            onClick={handleSubmit}
            disabled={loading || uploading}
            sx={{ minWidth: '140px' }}
          >
            {loading || uploading ? (
              <>
                <CircularProgress size={18} sx={{ mr: 1, color: 'white' }} />
                {uploading ? 'Upload...' : 'Création...'}
              </>
            ) : 'Créer la réunion'}
          </BaseButton>
        ) : (
          <BaseButton 
            variant="contained" 
            onClick={handleNext}
            sx={{ minWidth: '100px' }}
          >
            Suivant
          </BaseButton>
        )}
      </>
    );
  };

  return (
    <>
      <BaseDialog
        open={open}
        onClose={handleClose}
        title="🎙️ Nouvelle Réunion"
        subtitle="Créez une transcription à partir d'un fichier audio"
        maxWidth="sm"
        actions={getDialogActions()}
        customPaddingContent={{ py: 2 }}
      >
        <Stepper 
          activeStep={activeStep} 
          sx={{ 
            mb: 4,
            '& .MuiStepConnector-line': {
              borderColor: 'rgba(25, 118, 210, 0.1)',
              borderTopWidth: 2,
              borderRadius: 1
            },
            '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
              borderColor: '#1976d2'
            },
            '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
              borderColor: '#42a5f5'
            }
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel 
                sx={{ 
                  '& .MuiStepLabel-label': { 
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    color: 'text.secondary',
                    display: isMobile ? 'none' : 'block'
                  },
                  '& .MuiStepLabel-label.Mui-active': {
                    color: '#1976d2',
                    fontWeight: 600
                  },
                  '& .MuiStepIcon-root': {
                    fontSize: '1.8rem'
                  },
                  '& .MuiStepIcon-root.Mui-active': {
                    color: '#1976d2'
                  },
                  '& .MuiStepIcon-root.Mui-completed': {
                    color: '#1976d2'
                  }
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {getStepContent(activeStep)}
      </BaseDialog>

      <OneDriveFilePickerDialog
        open={oneDriveDialogOpen}
        onClose={() => setOneDriveDialogOpen(false)}
        onFileSelect={handleOneDriveFileSelected}
      />
    </>
  );
};

export default NewMeetingDialog; 